import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";

import {
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { ThirdWBSAPI } from "../services/api";
import html2pdf from "html2pdf.js";

// ======================================================
// HELPERS
// ======================================================

const formatNumber = (value: any) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "";
  }

  const num = Number(value);

  return Number.isFinite(num)
    ? num.toFixed(2)
    : "";
};

const getExcelValue = (value: any) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "";
  }

  const cleaned = String(value)
    .replace(/,/g, "")
    .trim();

  const parsed = parseFloat(cleaned);

  return isNaN(parsed)
    ? value
    : parsed;
};

const sanitizeSheetName = (
  name: string
) => {
  return (
    (name || "BOM")
      .replace(/[\\/*?:[\]]/g, " ")
      .substring(0, 31)
      .trim() || "BOM"
  );
};

const cleanText = (value: any) => {
  if (
    value === null ||
    value === undefined
  ) {
    return "";
  }

  return String(value)
    .replace(/&amp;/g, "&")
    .replace(/<br\s*\/?>/gi, "\n")
    .trim();
};

const getComponentTypeName = (componentType: any) => {
  const rawType = String(componentType ?? "").trim().toLowerCase();

  if (rawType === "material") return "Material";
  if (rawType === "equipment") return "Equipment";
  if (rawType === "manpower" || rawType === "labour") return "ManPower";

  const numericType = Number(componentType);
  if (numericType === 0) return "Material";
  if (numericType === 1 || numericType === 3) return "Equipment";
  if (numericType === 2) return "ManPower";

  return "Other";
};

// ======================================================
// MAIN PAGE
// ======================================================

const ActivityBOMPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const activityId = Number(
    searchParams.get("activityId") ??
    location.state?.activityId ??
    0
  );

  // ====================================================
  // PROJECT / WBS / ACTIVITY
  // ====================================================

  const projectId = Number(
    searchParams.get("projectId") ??
    location.state?.projectId ??
    0
  );

  const wbsId = Number(
    searchParams.get("wbsId") ??
    location.state?.wbsId ??
    location.state?.wbsNo ??
    0
  );

  const projectName =
    searchParams.get("projectName") ||
    location.state?.projectName ||
    "Project";

  const wbsName =
    searchParams.get("wbsName") ||
    location.state?.wbsName ||
    "WBS";

  const activity =
    location.state?.activity || {};

  const activityName =
    searchParams.get("activityName") ||
    activity?.activity ||
    activity?.name ||
    location.state?.activityName ||
    "Activity";

  // ====================================================
  // REFS
  // ====================================================

  const reportRef =
    useRef<HTMLDivElement>(null);

  // ====================================================
  // STATE
  // ====================================================

  const [bomData, setBomData] =
    useState<any[]>([]);

  const [loading, setLoading] =
    useState(true);

  const [exportingExcel, setExportingExcel] =
    useState(false);

  const [exportingPDF, setExportingPDF] =
    useState(false);

  // ====================================================
  // SUMMARIZE / GROUP BOM DATA
  // ====================================================
  //
  // Groups records using:
  // Component + Unit
  //
  // Example:
  // Finsand + CUM = 2.5
  // Finsand + CUM = 3.2
  // Finsand + CUM = 1.3
  //
  // Result:
  // Finsand + CUM = 7.0
  //
  // ====================================================

  const summarizedBomData = useMemo(() => {
    const groupedMap = new Map<string, any>();

    bomData.forEach((item: any) => {
      const component = cleanText(item.component ?? item.Component ?? "");
      const unitName = cleanText(
        item.unitName ?? item.UnitName ?? item.unit ?? ""
      );

      const rawComponentType =
        item.componentType ??
        item.ComponentType ??
        item.compType ??
        item.CompType ??
        "";

      const componentTypeName = getComponentTypeName(rawComponentType);

      const intentValue = Number(
        String(item.intent ?? item.Intent ?? 0)
          .replace(/,/g, "")
          .trim()
      );

      const numericIntent = Number.isFinite(intentValue)
        ? intentValue
        : 0;

      const groupKey =
        `${componentTypeName.toLowerCase()}|||` +
        `${component.toLowerCase()}|||` +
        `${unitName.toLowerCase()}`;

      if (groupedMap.has(groupKey)) {
        const existingItem = groupedMap.get(groupKey);
        existingItem.intent =
          Number(existingItem.intent || 0) + numericIntent;
      } else {
        groupedMap.set(groupKey, {
          ...item,
          component,
          unitName,
          componentTypeName,
          intent: numericIntent,
        });
      }
    });

    const typeOrder: Record<string, number> = {
      Material: 0,
     ManPower: 1,
      Equipment: 2,
      Other: 3,
    };

    return Array.from(groupedMap.values()).sort(
      (a: any, b: any) =>
        (typeOrder[a.componentTypeName] ?? 99) -
        (typeOrder[b.componentTypeName] ?? 99)
    );
  }, [bomData]);

  const groupedBomByType = useMemo(() => {
    const groups: Record<string, any[]> = {
      Material: [],
      Equipment: [],
      ManPower: [],
      Other: [],
    };

    summarizedBomData.forEach((item: any) => {
      const type = item.componentTypeName || "Other";
      if (!groups[type]) groups[type] = [];
      groups[type].push(item);
    });

    return groups;
  }, [summarizedBomData]);

  // ====================================================
  // LOAD ACTIVITY BOM
  // ====================================================

  useEffect(() => {
    if (!activityId) {
      setLoading(false);
      return;
    }

    if (
      !projectId ||
      !wbsId
    ) {
      console.error(
        "Activity BOM requires projectId and wbsId.",
        {
          projectId,
          wbsId,
          activityId,
        }
      );

      setBomData([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    ThirdWBSAPI.getActivityBOM(
      projectId,
      wbsId,
      Number(activityId)
    )
      .then((response: any) => {
        console.log(
          "Activity BOM response:",
          response
        );

        const data =
          response?.data ||
          response ||
          [];

        setBomData(
          Array.isArray(data)
            ? data
            : []
        );
      })
      .catch((error: any) => {
        console.error(
          "Activity BOM loading error:",
          error
        );

        setBomData([]);
      })
      .finally(() => {
        setLoading(false);
      });

  }, [
    activityId,
    projectId,
    wbsId,
  ]);

  // ====================================================
  // EXPORT PDF
  // ====================================================

  const exportToPDF = async () => {
    if (!reportRef.current) {
      return;
    }

    setExportingPDF(true);

    reportRef.current.classList.add(
      "pdf-exporting"
    );

    try {
      const options = {
        margin: 0.5,

        filename:
          `${projectName}_${activityName}_BOM.pdf`,

        image: {
          type: "jpeg" as const,
          quality: 1,
        },

        html2canvas: {
          scale: 2,
          useCORS: true,
          scrollX: 0,
          scrollY: 0,
        },

        jsPDF: {
          unit: "in" as const,
          format: "a4" as const,
          orientation: "landscape" as const,
        },

        pagebreak: {
          mode: [
            "avoid-all",
            "css",
            "legacy",
          ],
        },
      };

      await html2pdf()
        .set(options)
        .from(reportRef.current)
        .save();

    } catch (error) {
      console.error(
        "PDF export error:",
        error
      );

    } finally {
      reportRef.current.classList.remove(
        "pdf-exporting"
      );

      setExportingPDF(false);
    }
  };

  // ====================================================
  // EXPORT EXCEL
  // ====================================================

  const exportToExcel = async () => {
  setExportingExcel(true);

  try {
    const ExcelJS = await import("exceljs");
    const fileSaver: any = await import("file-saver");

    const workbook = new ExcelJS.Workbook();

    workbook.creator = "BaevrLite";
    workbook.created = new Date();

    const sheet = workbook.addWorksheet(
      sanitizeSheetName(activityName)
    );

    // Column widths
    sheet.columns = [
      { key: "component", width: 65 },
      { key: "unit", width: 18 },
      { key: "intent", width: 18 },
    ];

    sheet.properties.defaultRowHeight = 20;

    // =====================================================
    // TITLE
    // =====================================================

    sheet.mergeCells("A1:C1");
    const titleCell = sheet.getCell("A1");
    titleCell.value = `BOM for ${projectName}`;

    titleCell.font = {
      bold: true,
      size: 14,
    };

    titleCell.alignment = {
      horizontal: "left",
      vertical: "middle",
    };

    sheet.getRow(1).height = 25;

    // =====================================================
    // ACTIVITY
    // =====================================================

    sheet.mergeCells("A2:C2");
    const activityCell = sheet.getCell("A2");
    activityCell.value = `Activity: ${activityName}`;

    activityCell.font = {
      bold: true,
      size: 11,
    };

    activityCell.alignment = {
      horizontal: "left",
      vertical: "middle",
    };

    sheet.getRow(2).height = 22;

    let rowIndex = 4;

    // Section order
    const sectionOrder = [
      "Material",
      "Equipment",
      "ManPower",
      "Other",
    ];

    sectionOrder.forEach((sectionName) => {
      const items = groupedBomByType[sectionName] || [];

      // Do not display empty sections
      if (items.length === 0) return;

      // =====================================================
      // SECTION TITLE
      // =====================================================

      const sectionRow = sheet.getRow(rowIndex);

      sheet.mergeCells(`A${rowIndex}:C${rowIndex}`);

      const sectionCell = sheet.getCell(`A${rowIndex}`);
      sectionCell.value = sectionName.toUpperCase();

      sectionCell.font = {
        bold: true,
        color: {
          argb: "FFFFFFFF",
        },
      };

      sectionCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: {
          argb: "FF343A40",
        },
      };

      sectionCell.alignment = {
        horizontal: "left",
        vertical: "middle",
      };

      sectionRow.height = 24;

      rowIndex++;

      // =====================================================
      // SECTION HEADER
      // =====================================================

      const headerRow = sheet.getRow(rowIndex);

      headerRow.values = [
        "Component",
        "Unit",
        "Intent",
      ];

      headerRow.height = 22;

      headerRow.eachCell((cell: any, columnNumber: number) => {
        cell.font = {
          bold: true,
          color: {
            argb: "FFFFFFFF",
          },
        };

        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: {
            argb: "FF6C757D",
          },
        };

        cell.alignment = {
          horizontal: columnNumber === 1 ? "left" : "center",
          vertical: "middle",
        };

        cell.border = {
          top: {
            style: "thin",
            color: {
              argb: "FF8A8A8A",
            },
          },
          bottom: {
            style: "thin",
            color: {
              argb: "FF8A8A8A",
            },
          },
          left: {
            style: "thin",
            color: {
              argb: "FF8A8A8A",
            },
          },
          right: {
            style: "thin",
            color: {
              argb: "FF8A8A8A",
            },
          },
        };
      });

      rowIndex++;

      // =====================================================
      // SECTION DATA ROWS
      // =====================================================

      items.forEach((item: any) => {
        const row = sheet.getRow(rowIndex);

        row.getCell(1).value = cleanText(item.component);
        row.getCell(2).value = cleanText(item.unitName);
        row.getCell(3).value = getExcelValue(item.intent);

        row.height = 20;

        row.getCell(1).alignment = {
          horizontal: "left",
          vertical: "middle",
          wrapText: true,
        };

        row.getCell(2).alignment = {
          horizontal: "center",
          vertical: "middle",
        };

        row.getCell(3).alignment = {
          horizontal: "right",
          vertical: "middle",
        };

        row.getCell(3).numFmt = "#,##0.00";

        // Bottom borders
        for (let column = 1; column <= 3; column++) {
          row.getCell(column).border = {
            bottom: {
              style: "thin",
              color: {
                argb: "FFD9D9D9",
              },
            },
          };
        }

        rowIndex++;
      });

      // Blank row between sections
      rowIndex++;
    });

    // Freeze top title/activity area
    sheet.views = [
      {
        state: "frozen",
        ySplit: 3,
      },
    ];

    // =====================================================
    // SAVE FILE
    // =====================================================

    const buffer = await workbook.xlsx.writeBuffer();

    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    fileSaver.saveAs(
      blob,
      `${projectName}_${activityName}_BOM.xlsx`
    );
  } catch (error) {
    console.error("Excel export error:", error);
  } finally {
    setExportingExcel(false);
  }
};

  // ====================================================
  // LOADING
  // ====================================================

  if (loading) {
    return (
      <div
        style={{
          padding: "20px",
          fontFamily:
            "Arial, sans-serif",
        }}
      >
        Loading BOM...
      </div>
    );
  }

  // ====================================================
  // UI
  // ====================================================

  return (
    <div
      className="activity-bom-page"
      style={{
        padding: "20px",
        fontFamily:
          "Arial, sans-serif",
        background: "#fff",
        minHeight: "100vh",
      }}
    >

      {/* =================================================
          PDF CSS
      ================================================= */}

      <style>
        {`
          .pdf-exporting button,
          .pdf-exporting .pdf-hide {
            display: none !important;
          }

          .pdf-exporting {
            width: 100% !important;
            max-width: 900px !important;
            margin: 0 auto !important;
            background: #ffffff !important;
          }

          .pdf-exporting .bom-row:hover {
            background: inherit !important;
          }

          .bom-row:hover {
            background: #f5f5f5 !important;
          }

          .bom-report-container {
            width: 100%;
            max-width: 900px;
            overflow-x: auto;
          }

          .bom-section-header,
          .bom-section-row {
            display: grid;
            grid-template-columns: minmax(0, 1fr) 120px 150px;
          }

          .bom-section-header {
            min-width: 0 !important;
          }

          .bom-section-row {
            min-width: 0 !important;
          }

          @media (max-width: 768px) {
            .activity-bom-page {
              padding: 10px !important;
              overflow-x: hidden;
            }

            .activity-bom-header {
              position: static !important;
              display: flex !important;
              justify-content: center !important;
              width: 100%;
              margin-bottom: 12px !important;
            }

            .activity-bom-actions {
              position: static !important;
              width: 100%;
              justify-content: center;
              gap: 8px !important;
            }

            .activity-bom-actions button {
              flex: 1;
              min-width: 120px;
              padding: 9px 10px !important;
              font-size: 12px !important;
            }

            .activity-bom-title {
              font-size: 16px !important;
              line-height: 1.35;
              max-width: 100%;
              overflow-wrap: anywhere;
            }

            .bom-report-container {
              max-width: 100% !important;
              overflow-x: hidden !important;
            }

            .bom-section {
              width: 100% !important;
              margin-bottom: 16px !important;
            }

            .bom-section-title {
              font-size: 13px !important;
              padding: 8px 10px !important;
            }

            .bom-section-header,
            .bom-section-row {
              grid-template-columns: minmax(0, 1fr) 65px 75px !important;
              min-width: 0 !important;
              width: 100% !important;
            }

            .bom-section-header {
              padding: 8px 6px !important;
              font-size: 11px !important;
            }

            .bom-section-row {
              padding: 8px 6px !important;
              font-size: 11px !important;
            }

            .bom-component {
              min-width: 0;
              overflow-wrap: anywhere;
              word-break: break-word;
              line-height: 1.35;
            }
          }

          @media (max-width: 480px) {
            .activity-bom-page {
              padding: 6px !important;
            }

            .activity-bom-title {
              font-size: 14px !important;
            }

            .activity-bom-actions button {
              min-width: 0;
              font-size: 11px !important;
              padding: 8px 6px !important;
            }

            .bom-section-header,
            .bom-section-row {
              grid-template-columns: minmax(0, 1fr) 52px 58px !important;
            }

            .bom-section-header,
            .bom-section-row {
              font-size: 10px !important;
            }
          }
        `}
      </style>

      {/* =================================================
          PAGE HEADER
      ================================================= */}

      <div
        className="activity-bom-header"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginBottom: "20px",
          gap: "15px",
          flexWrap: "wrap",
          position: "relative",
        }}
      >

        {/* =================================================
            EXPORT BUTTONS
        ================================================= */}

        <div
          className="pdf-hide activity-bom-actions"
          style={{
            position: "absolute",
            right: "0",
            top: "0",
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >

          <button
            onClick={exportToExcel}
            disabled={
              exportingExcel ||
              summarizedBomData.length === 0
            }
            style={{
              background:
                exportingExcel ||
                summarizedBomData.length === 0
                  ? "#6c757d"
                  : "#198754",

              color: "#fff",
              border: "none",
              padding: "10px 16px",
              borderRadius: "6px",

              cursor:
                exportingExcel ||
                summarizedBomData.length === 0
                  ? "default"
                  : "pointer",

              fontWeight: 600,
            }}
          >
            {exportingExcel
              ? "Exporting..."
              : "Export Excel"}
          </button>

          <button
            onClick={exportToPDF}
            disabled={
              exportingPDF ||
              summarizedBomData.length === 0
            }
            style={{
              background:
                exportingPDF ||
                summarizedBomData.length === 0
                  ? "#6c757d"
                  : "#dc3545",

              color: "#fff",
              border: "none",
              padding: "10px 16px",
              borderRadius: "6px",

              cursor:
                exportingPDF ||
                summarizedBomData.length === 0
                  ? "default"
                  : "pointer",

              fontWeight: 600,
            }}
          >
            {exportingPDF
              ? "Exporting..."
              : "Export PDF"}
          </button>

        </div>

      </div>

      {/* =================================================
          REPORT
      ================================================= */}

      <div
        ref={reportRef}
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >

        {/* =================================================
            REPORT TITLE
        ================================================= */}

        <div
          style={{
            marginBottom: "15px",
            textAlign: "center",
          }}
        >

          {/* PROJECT NAME */}

          <div
            className="activity-bom-title"
            style={{
              fontSize: "18px",
              fontWeight: "bold",
              marginBottom: "5px",
            }}
          >
            {projectName}
          </div>

          {/* WBS AND ACTIVITY */}

          <div
            style={{
              fontSize: "13px",
              color: "#666",
            }}
          >
            WBS: <strong>{wbsName}</strong>

            <span
              style={{
                marginLeft: "30px",
              }}
            >
              Activity:{" "}
              <strong>
                {activityName}
              </strong>
            </span>
          </div>

          {/* REPORT TITLE */}

          <div
            style={{
              fontSize: "18px",
              fontWeight: "bold",
              marginTop: "15px",
            }}
          >
            Bill of Materials
          </div>

        </div>

        {/* =================================================
            GROUPED BOM SECTIONS
        ================================================= */}

        <div
          className="bom-report-container"
          style={{
            width: "100%",
            maxWidth: "900px",
            overflowX: "auto",
          }}
        >
          {summarizedBomData.length === 0 ? (
            <div
              style={{
                padding: "25px",
                textAlign: "center",
                color: "#777",
                border: "1px solid #ccc",
              }}
            >
              No BOM data found
            </div>
          ) : (
            <>
              {["Material", "Equipment","ManPower", "Other"].map((type) => {
                const items = groupedBomByType[type] || [];

                if (items.length === 0) return null;

                return (
                  <div
                    key={type}
                    className="bom-section"
                    style={{
                      marginBottom: "25px",
                      width: "100%",
                    }}
                  >
                    <div
                      className="bom-section-title"
                      style={{
                        background: "#343a40",
                        color: "#fff",
                        padding: "10px 12px",
                        fontSize: "15px",
                        fontWeight: "bold",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        borderRadius: "5px 5px 0 0",
                      }}
                    >
                      {type}
                    </div>

                    <div
                      className="bom-section-header"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 120px 150px",
                        background: "#6c757d",
                        color: "#fff",
                        padding: "9px 10px",
                        fontWeight: "bold",
                        fontSize: "13px",
                        minWidth: "650px",
                      }}
                    >
                      <div>Component</div>
                      <div style={{ textAlign: "center" }}>Unit</div>
                      <div style={{ textAlign: "right" }}>Intent</div>
                    </div>

                    <div
                      style={{
                        border: "1px solid #ccc",
                        borderTop: "none",
                        overflowX: "auto",
                      }}
                    >
                      {items.map((item: any, index: number) => (
                        <div
                          key={`${type}-${cleanText(item.component)}-${cleanText(item.unitName)}-${index}`}
                          className="bom-row bom-section-row"
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 120px 150px",
                            padding: "9px 10px",
                            borderBottom: "1px solid #eee",
                            background: index % 2 === 0 ? "#fff" : "#fafafa",
                            minWidth: "650px",
                            fontSize: "13px",
                          }}
                        >
                          <div className="bom-component" style={{ color: "#444", lineHeight: "1.5" }}>
                            {cleanText(item.component)}
                          </div>

                          <div style={{ textAlign: "center", color: "#333" }}>
                            {cleanText(item.unitName)}
                          </div>

                          <div
                            style={{
                              textAlign: "right",
                              color: "#333",
                              fontWeight: 500,
                            }}
                          >
                            {formatNumber(item.intent)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>

    </div>
  );
};

export default ActivityBOMPage;